import type { AccountIRRWindow, Horizons } from "wealthmode-frontend"

const ABSENT = "--"
const DISPLAYED_PLACES = 1

/**
 * An account's IRR as "{rate}/{months}mos": its annualized rate over the whole span measured, and
 * that span rounded to whole months where there is a window to say so.
 */
export function formatIRR(irr: Horizons | null, window: AccountIRRWindow | null): string {
  if (irr == null) return ABSENT
  if (window == null) return percent(irr.max)

  const months = windowMonths(window)

  return `${percent(irr.max)}/${months}${months === 1 ? "mo" : "mos"}`
}

/**
 * An account's IRR as formatIRR gives it, followed by " | {market}": the market's annualized return
 * over the same days, absent where there is none.
 */
export function formatIRRAgainstMarket(
  irr: Horizons | null,
  window: AccountIRRWindow | null,
  market: number | null,
): string {
  if (irr == null) return ABSENT

  return `${formatIRR(irr, window)} | ${market == null ? ABSENT : percent(market)}`
}

/** How an account's IRR stands against the market's over the same days. */
export type MarketComparison = "ahead" | "near" | "level" | "behind"

// An account clear of the market over the same days reads good, one close to it weak, and one clear
// behind it bad.
export const IRR_COLORS: Record<MarketComparison, string> = {
  ahead: "text-grade-good",
  near: "text-grade-weak",
  level: "text-foreground-secondary",
  behind: "text-grade-bad",
}

// Rates this many percentage points apart or closer read as near the market.
const NEAR_POINTS = 0.5

/**
 * Whether the account returned more than the market over the same days, less, within NEAR_POINTS
 * either way, or the same as displayed, which is also the reading wherever either side is absent.
 */
export function compareIRRToMarket(irr: Horizons | null, market: number | null): MarketComparison {
  if (irr == null || market == null) return "level"

  const points = Number((displayed(irr.max) - displayed(market)).toFixed(DISPLAYED_PLACES))
  if (points === 0) return "level"
  if (Math.abs(points) <= NEAR_POINTS) return "near"

  return points > 0 ? "ahead" : "behind"
}

const DAYS_PER_MONTH = 365.25 / 12

/** How many months a window spans, to the nearest whole month. */
function windowMonths(window: AccountIRRWindow): number {
  const days = (Date.parse(window.end) - Date.parse(window.start)) / 86_400_000

  return Math.round(days / DAYS_PER_MONTH)
}

function displayed(rate: number): number {
  return Number((rate * 100).toFixed(DISPLAYED_PLACES))
}

function percent(rate: number): string {
  return `${displayed(rate).toFixed(DISPLAYED_PLACES)}%`
}

/** Whether an adjusted IRR is worth showing beneath the IRR: only where it reads differently. */
export function showsAdjusted(irr: Horizons | null, adjusted: Horizons | null): boolean {
  return adjusted != null && formatIRR(adjusted, null) !== formatIRR(irr, null)
}

/**
 * The color of how far a value series can be trusted, given as a fraction: good from 90%, weak from
 * 80%, and bad below that.
 */
export function confidenceColor(confidence: string | null): string {
  if (confidence == null) return "text-foreground-secondary"

  const value = Number(confidence)
  if (value >= 0.9) return "text-grade-good"
  if (value >= 0.8) return "text-grade-weak"

  return "text-grade-bad"
}
